import React, { useMemo } from "react";
import { FaInfoCircle } from "react-icons/fa";

// ----------------------------------------------
// Tooltip component (shared between boarding and non-boarding)
// ----------------------------------------------
const DiscountTooltip = ({
  activeDiscount,
  discountAmountDisplay,
  valueDisplay,
  discountList,
  tooltipId,
}) => {
  return (
    <div
      id={tooltipId}
      role="tooltip"
      className="pointer-events-none absolute z-10 w-64 p-2 text-sm leading-tight text-white bg-gray-700 rounded shadow-lg opacity-0 scale-95 group-hover:opacity-100 group-focus:opacity-100 group-hover:scale-100 group-focus:scale-100 right-full mr-2 top-1/2 -translate-y-1/2 transform-gpu transition-all duration-200"
      style={{ whiteSpace: "pre-line" }}
    >
      {activeDiscount ? (
        <>
          <div className="font-semibold">
            {activeDiscount.name ?? "Active Discount"} Applied
          </div>
          {discountAmountDisplay && (
            <div className="text-xs">
              Discount amount: {discountAmountDisplay}
              {valueDisplay && <span> ({valueDisplay})</span>}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="font-semibold">Available discounts</div>
          <div className="text-xs mt-1">
            {discountList || "No discount data"}
          </div>
        </>
      )}
    </div>
  );
};

// ----------------------------------------------
// Main component
// ----------------------------------------------
const PriceWithDiscount = ({
  item,
  formatName = (name) => name, // default: return as is
}) => {
  // ----- Derived values (useMemo for performance) -----
  const isBoarding = item?.item_type === "boarding";
  const duration = item?.duration || 1;
  const taxAmount = item?.tax_amount || 0;
  // const taxAmountBeforeDiscount =
  // item?.tax_amount_before_order_discount || 0;

  const originalPricePerDay = useMemo(() => {
    return isBoarding ? item?.base_total / duration : item?.base_total;
  }, [item?.base_total, isBoarding, duration]);

  const originalTotal = useMemo(() => {
    return item.base_total;
  }, [item.base_total]);

  const amountWithoutTax = useMemo(() => {
    return Number(item?.unit_price || item?.amount_after_discount || 0);
  }, [item]);

  const discountedPricePerDay = useMemo(() => {
    if (isBoarding && duration > 0) {
      return amountWithoutTax / duration;
    }
    return amountWithoutTax;
  }, [isBoarding, duration, amountWithoutTax]);

  // ----- Discount data -----
  const discountArray = item?.discounts || item?.discount || [];
  const activeDiscount = discountArray.find((d) => Number(d.is_active) === 1);
  const hasAnyDiscount = discountArray.length > 0;
  const hasActiveDiscount = Boolean(activeDiscount);
  const hasPriceDifference =
    hasAnyDiscount &&
    item.base_total > 0 &&
    item.unit_price !== item.base_total;

  const discountAmount =
    item?.discount_amount || activeDiscount?.discount_amount || 0;
  const discountAmountDisplay =
    discountAmount != null ? `₹ ${Number(discountAmount).toFixed(2)}` : null;

  const valueDisplay = activeDiscount
    ? activeDiscount.type === "percentage"
      ? `${activeDiscount.value}%`
      : `₹ ${Number(activeDiscount.value ?? 0).toFixed(2)}`
    : null;

  const discountList = useMemo(() => {
    return discountArray
      .map((d, i) => {
        const name = d?.name ?? `Discount #${i + 1}`;
        const typeLabel =
          d?.type === "percentage"
            ? `${d?.value}%`
            : `₹ ${Number(d?.value ?? 0).toFixed(2)}`;
        const amount = `₹ ${Number(d?.discount_amount ?? 0).toFixed(2)}`;
        const activeFlag = Number(d?.is_active) === 1 ? " (active)" : "";
        return `${name} — ${typeLabel} (${amount})${activeFlag}`;
      })
      .join("\n");
  }, [discountArray]);

  const tooltipId = useMemo(() => {
    const idBase = item?.id ?? item?._id ?? Math.floor(Math.random() * 100000);
    return `discount-tooltip-${idBase}`;
  }, [item]);

  // ----- Render logic -----
  if (isBoarding) {
    // Boarding items: show per‑day breakdown
    return (
      <>
        {hasPriceDifference ? (
          <div className="flex flex-col">
            {/* Original (strikethrough) */}
            <span className="text-gray-500 line-through text-sm">
              {/* {duration} × ₹{Number(originalPricePerDay).toFixed(2)} =  */}₹
              {Number(originalTotal).toFixed(2)}
            </span>

            {/* Discounted + tooltip */}
            <div className="flex items-center gap-2">
              {/* Icon */}
              <div className="relative inline-flex shrink-0">
                <span className="relative inline-flex group shrink-0">
                  <FaInfoCircle
                    className="text-sm text-gray-500 cursor-pointer"
                    tabIndex={0}
                    aria-describedby={tooltipId}
                  />

                  <div
                    id={tooltipId}
                    role="tooltip"
                    className="absolute z-10 w-64 p-2 text-sm leading-tight text-white bg-gray-700 rounded shadow-lg opacity-0 pointer-events-none transition-all duration-200 top-full left-1/2 -translate-x-1/2 mt-2 group-hover:opacity-100 group-focus:opacity-100"
                    style={{ whiteSpace: "pre-line" }}
                  >
                    {activeDiscount ? (
                      <>
                        <div className="font-semibold">
                          {formatName(activeDiscount?.name) ??
                            "Active Discount"}{" "}
                          Applied
                        </div>
                        {discountAmountDisplay && (
                          <div className="text-xs">
                            Discount amount: {discountAmountDisplay}
                            {valueDisplay && <span> ({valueDisplay})</span>}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="font-semibold">Available discounts</div>
                        <div className="text-xs mt-1">
                          {discountList || "No discount data"}
                        </div>
                      </>
                    )}
                  </div>
                </span>
              </div>

              {/* Price */}
              <div className="flex flex-col">
                <span className="text-green-600 font-bold">
                  ₹{(Number(amountWithoutTax) * duration).toFixed(2)}
                </span>
                {taxAmount > 0 && (
                  <span className="text-sm text-gray-600">
                    +₹{taxAmount.toFixed(2)} taxes included
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : (
          // No discount or no price difference – just show the total
          <div className="flex flex-col">
            <span className="text-green-600 font-bold">
              ₹{Number(originalTotal).toFixed(2)}
            </span>
            {taxAmount > 0 && (
              <span className="text-sm text-gray-600">
                +₹{taxAmount.toFixed(2)} taxes included
              </span>
            )}
          </div>
        )}
      </>
    );
  }

  // Non‑boarding items: show single price
  return (
    <>
      {/* Original price (strikethrough if discounted) */}
      <span
        className={
          hasPriceDifference
            ? "line-through text-gray-500 text-sm"
            : "text-green-600 font-bold"
        }
      >
        ₹ {Number(originalTotal).toFixed(2)}
      </span>

      {/* Discounted price with tooltip */}
      {hasPriceDifference ? (
        <div className="flex items-center gap-2 ml-2">
          {/* Icon */}
          <div className="relative inline-flex shrink-0">
            <span className="relative inline-flex group shrink-0">
              <FaInfoCircle
                className="text-sm text-gray-500 cursor-pointer"
                tabIndex={0}
                aria-describedby={tooltipId}
              />

              <div
                id={tooltipId}
                role="tooltip"
                className="absolute z-10 w-64 p-2 text-sm leading-tight text-white bg-gray-700 rounded shadow-lg opacity-0 pointer-events-none transition-all duration-200 top-full left-1/2 -translate-x-1/2 mt-2 group-hover:opacity-100 group-focus:opacity-100"
                style={{ whiteSpace: "pre-line" }}
              >
                {activeDiscount ? (
                  <>
                    <div className="font-semibold">
                      {formatName(activeDiscount?.name) ?? "Active Discount"}{" "}
                      Applied
                    </div>
                    {discountAmountDisplay && (
                      <div className="text-xs">
                        Discount amount: {discountAmountDisplay}
                        {valueDisplay && <span> ({valueDisplay})</span>}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="font-semibold">Available discounts</div>
                    <div className="text-xs mt-1">
                      {discountList || "No discount data"}
                    </div>
                  </>
                )}
              </div>
            </span>
          </div>

          {/* Prices */}
          <div className="flex flex-col">
            <span className="text-green-600 font-bold">
              ₹{Number(amountWithoutTax).toFixed(2)}
            </span>
            {taxAmount > 0 && (
              <span className="text-sm text-gray-600">
                +₹{taxAmount.toFixed(2)} taxes included
              </span>
            )}
          </div>
        </div>
      ) : taxAmount > 0 && (
        <span className="text-sm text-gray-600">
          +₹{taxAmount.toFixed(2)} taxes included
        </span>
      )}
    </>
  );
};

export default PriceWithDiscount;